<template>
  <div
    v-if="isLoading"
    class="w-full mt-2 flex justify-center"
  >
    <BaseSpinner />
  </div>
  <section v-else>
    <div
      v-if="!visibleDegreeMaps.length"
      class="text-center text-gray-500 bg-gray-200 rounded p-3 mb-2 border"
    >
      No Degree Maps
    </div>
    <template v-else>
      <ExpandableController
        v-if="degreeMapsRedesigned"
        :expanded="expanded"
        @toggle="expanded = $event"
      >
        <template #default="{ expandables, trackToggle }">
          <DegreeMapV2
            v-for="(degreeMap,index) in visibleDegreeMaps"
            :key="index"
            :value="degreeMap"
            :document-value="documentValue"
            :config="config"
            :courses="courses"
            :course-sets="courseSets"
            :requirement-groups="requirementGroups"
            :terms="terms"
            :semester-names="semesterNames"
            :expandable-id="index"
            :expanded="index in expandables ? expandables[index] : expanded"
            @toggle="trackToggle({ expandableId: index, expandableStatus: $event })"
          />
        </template>
      </ExpandableController>
      <template v-else>
        <DegreeMapV1
          v-for="(degreeMap,index) in visibleDegreeMaps"
          :key="index"
          :value="degreeMap"
          :document-value="documentValue"
          :config="config"
          :courses="courses"
          :course-sets="courseSets"
          :requirement-groups="requirementGroups"
          :terms="terms"
          :semester-names="semesterNames"
        />
      </template>
    </template>
  </section>
</template>

<script>
import { mapGetters } from 'vuex'
import _flatten from 'lodash/flatten'
import DegreeMapV1 from './DegreeMap'
import DegreeMapV2 from './DegreeMapsV2/DegreeMap'
import ExpandableController from '@/components/ExpandableController'
import { getEffectiveDatesRangeAsString } from '@/utils/effectiveDatesRange'
import useFeatureFlags from '@/composables/useFeatureFlags'
import { DEGREEMAP_REDESIGNED, EXPAND_ALL_DM_REQUISITE } from '@/constants/featureFlags'

export default {
  components: {
    ExpandableController,
    DegreeMapV1,
    DegreeMapV2,
  },
  inject: ['terms', 'semesterNames'],
  props: {
    value: {
      type: Array,
      default: () => [],
    },
    documentValue: {
      type: Object,
      default: () => ({}),
    },
    config: {
      type: Object,
      default: () => ({}),
    },
  },
  setup() {
    const { degreeMapsRedesigned, expandAllDMRequisite } = useFeatureFlags({
      degreeMapsRedesigned: DEGREEMAP_REDESIGNED,
      expandAllDMRequisite: EXPAND_ALL_DM_REQUISITE,
    })
    return { 
      degreeMapsRedesigned,
      expanded: expandAllDMRequisite,
    }
  },
  data() {
    return {
      isLoading: false,
      courses: [],
      courseSets: [],
      requirementGroups: [],
    }
  },
  computed: {
    ...mapGetters('settings', [
      'enableFetchingDegreeMapsCoursesWithLatestRevisions',
    ]),
    visibleDegreeMaps() {
      return (this.value || [])
        .filter((dm) => dm.displayedInCatalog)
        .sort((a, b) => (!a.defaultDegreeMap && b.defaultDegreeMap ? 1 : -1))
    },
    rules() {
      return (this.value || []).reduce((rules, { semesters = [] }) => {
        semesters.forEach(({ requirements = [] }) => {
          requirements.forEach(({ requirementSelect }) => {
            rules.push(..._flatten(requirementSelect))
          })
        })
        return rules
      }, [])
    },
    courseGroupIds() {
      const arr = this.rules
        .filter((item) => item.type === 'course' || item.type === 'courses')
        .map((item) => item.value)
      return [...new Set(arr)].filter(Boolean).join(',')
    },
    courseSetGroupIds() {
      const arr = this.rules
        .filter((item) => item.type === 'courseSets')
        .map((item) => item.value)
      return [...new Set(arr)].filter(Boolean).join(',')
    },
    requirementGroupIds() {
      const idsFromSets = this.courseSets.flatMap(
        (set) => set.requirementGroups || []
      )
      const idsFromCourses = this.courses
        .map((c) => c.requirementGroup)
        .filter(Boolean)
      return [...new Set([...idsFromCourses, ...idsFromSets])]
    },
  },
  watch: {
    async requirementGroupIds(ids) {
      if (!ids) return
      const response = await Promise.all(
        ids.map((id) =>
          this.$axios.$get(
            `/${this.$store.state.settings.school}/requirementGroups/${id}`
          )
        )
      )
      this.requirementGroups = response
        .map((r) => Object.values(r.data)[0])
        .filter(Boolean)
    },
  },
  async created() {
    try {
      this.isLoading = true
      const school = this.$store.state.settings.school
      const dates = getEffectiveDatesRangeAsString(this.documentValue)
      const courseGroupIds = this.courseGroupIds
      const courseSetGroupIds = this.courseSetGroupIds
      const [courses, courseSets] = await Promise.all(
        [
          courseGroupIds
            ? this.$axios.$get(
                `/cm/${school}/courses?courseGroupIds=${courseGroupIds}&effectiveDatesRange=${dates}&useFetchingDegreeMapsCoursesLatestRevisionSettings=${
                  this.enableFetchingDegreeMapsCoursesWithLatestRevisions
                }`
              )
            : Promise.resolve({}),
          courseSetGroupIds
            ? this.$axios.$get(
                `/cm/${school}/courseSets?courseSetGroupIds=${courseSetGroupIds}&effectiveDatesRange=${dates}&includeAggregatedCoursesData=true`
              )
            : Promise.resolve({}),
        ].filter(Boolean)
      )
      this.courses = Object.values(courses)
      this.courseSets = Object.values(courseSets.courseSets || {}).map(
        (set) => ({
          ...(courseSets.aggregatedCoursesData.find(
            (data) => data.id === set.id
          ) || {}),
          ...set,
        })
      )
      this.isLoading = false
    } catch (e) {
      this.$bugsnag.notify(e)
      console.warn('Can not load degree maps data')
    }
  },
}
</script>
